/**
 * Starting a payment from the browser — handover rev7 §9.4 steps 1 to 3.
 *
 * The page submits the order through `submit_order`, which answers with
 * `payment.next`. This is the call that follows it:
 *
 *   card lane      → `payment_start` reserves the full gross and answers with
 *                    the payment page URL. The page goes there (§9.4 step 2).
 *   transfer lane  → `payment_start` moves the order to `awaiting_payment`
 *                    and queues the díjbekérő; U2 carries the bank details.
 *                    The page stays where it is and says so.
 *
 * `payment_mock` is the test API of the mock driver: the mock payment page's
 * three buttons go through it, and it posts a signed IPN to the real IPN
 * endpoint. With `PAYMENT_DRIVER=simplepay` it refuses, and the buttons it
 * serves are not on screen, because the customer is on SimplePay's own page.
 *
 * The publishable key goes on `apikey` and only there (`lib/api.ts`).
 */

import { supabaseFetch } from './api.ts'
import type { PublicConfig } from './env.ts'

export interface PaymentStartAnswer {
  ok: boolean
  method?: 'card' | 'transfer'
  order?: { ref: string; status?: string }
  amount_gross?: number
  payment_url?: string
  payment_deadline?: string | null
  attempt?: number
  next?: 'redirect_to_payment_page' | 'await_transfer'
  code?: string
  message?: string
}

async function call(
  config: PublicConfig,
  name: string,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<PaymentStartAnswer> {
  let response: Response
  try {
    response = await supabaseFetch(
      config.supabaseUrl,
      `/functions/v1/${name}`,
      {
        method: 'POST',
        apiKey: config.supabasePublishableKey,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
      fetchImpl,
    )
  } catch {
    return { ok: false, code: 'transport' }
  }
  const answer = (await response.json().catch(() => ({}))) as PaymentStartAnswer
  if (!response.ok && answer.code === undefined) return { ok: false, code: 'transport' }
  return answer
}

/** §9.4 step 1. The amount is the server's, never the page's. */
export const startPayment = (
  config: PublicConfig,
  token: string,
  fetchImpl: typeof fetch = fetch,
  options: { simplepayNotice?: boolean } = {},
): Promise<PaymentStartAnswer> =>
  call(
    config,
    'payment_start',
    // SP doc §8: the data-transfer notice, ticked. `payment_start` records it
    // with its time and starts nothing on the SimplePay route without it.
    options.simplepayNotice === true ? { token, simplepay_notice: true } : { token },
    fetchImpl,
  )

/**
 * SimplePay test prompt §1: whether this email takes the SimplePay route. The
 * server answers true or false and nothing else; any failure reads as false,
 * which is the díjbekérő route the page shows anyway.
 */
export async function simplePayCheck(
  config: PublicConfig,
  email: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await supabaseFetch(
      config.supabaseUrl,
      '/functions/v1/payment_start',
      {
        method: 'POST',
        apiKey: config.supabasePublishableKey,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'simplepay_check', email }),
      },
      fetchImpl,
    )
    const answer = (await response.json().catch(() => ({}))) as { simplepay?: unknown }
    return answer.simplepay === true
  } catch {
    return false
  }
}

/** The mock driver's outcomes, which are §9.4's own four back-URL results. */
export type MockOutcome = 'success' | 'fail' | 'cancel' | 'timeout'

export interface MockDriveAnswer {
  ok: boolean
  ipn_status?: number
  code?: string
}

/**
 * Drives one outcome through the mock test API, which posts the IPN to the
 * real endpoint. Nothing about this is a payment: no money moves and the page
 * it is called from says so on every screen.
 */
export const driveMockPayment = (
  config: PublicConfig,
  providerRef: string,
  outcome: MockOutcome,
  amount: number,
  fetchImpl: typeof fetch = fetch,
): Promise<MockDriveAnswer> =>
  call(config, 'payment_mock', {
    provider_ref: providerRef,
    outcome,
    amount,
  }, fetchImpl) as Promise<MockDriveAnswer>
